import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getApiBaseUrl } from '@/lib/api';
import { ThemeSettings } from '@/contexts/SiteSettingsContext';
import { QUERY_KEYS } from '@/lib/queryKeys';
import { emitAuthSessionEvent } from '@/lib/auth/sessionCoordinator';

const API_URL = getApiBaseUrl();
const THEME_CACHE_KEY = 'theme:v1';
const THEME_TTL_MS = 24 * 60 * 60 * 1000;

type ThemeCachePayload = {
  version: 1;
  savedAt: number;
  data: ThemeSettings;
};

const loadCachedThemePayload = (): ThemeCachePayload | null => {
  try {
    const raw = localStorage.getItem(THEME_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ThemeCachePayload;
    if (!parsed || parsed.version !== 1 || !parsed.data || !parsed.savedAt) return null;
    return parsed;
  } catch {
    return null;
  }
};

const loadCachedTheme = (allowStale = false): ThemeSettings | null => {
  const payload = loadCachedThemePayload();
  if (!payload) return null;
  if (!allowStale && Date.now() - payload.savedAt > THEME_TTL_MS) return null;
  return payload.data;
};

const saveCachedTheme = (theme: ThemeSettings): void => {
  try {
    const payload: ThemeCachePayload = {
      version: 1,
      savedAt: Date.now(),
      data: theme,
    };
    localStorage.setItem(THEME_CACHE_KEY, JSON.stringify(payload));
  } catch {
    // no-op
  }
};

const defaultTheme: ThemeSettings = {
  primary_color: '207 90% 42%',
  accent_color: '187 85% 43%',
  success_color: '160 84% 39%',
  warning_color: '38 92% 50%',
  sidebar_bg_color: '210 50% 15%',
  is_dark_mode: false,
};

/**
 * Fetch theme from API (public endpoint, no auth required)
 */
const fetchTheme = async (): Promise<ThemeSettings> => {
  try {
    const response = await fetch(`${API_URL}/settings/theme`);
    if (response.status >= 400) {
      if (response.status >= 500) {
        emitAuthSessionEvent({
          type: 'SERVER_ERROR',
          status: response.status,
          source: 'useThemeQuery:/settings/theme',
          at: Date.now(),
          message: `Theme API ${response.status}`,
        });
      }
      const fallback = loadCachedTheme(true);
      if (fallback) return fallback;
      return defaultTheme;
    }
    const data = await response.json();
    if (data.success && data.data) {
      const theme = data.data as ThemeSettings;
      saveCachedTheme(theme);
      return theme;
    }
    return loadCachedTheme(true) || defaultTheme;
  } catch {
    emitAuthSessionEvent({
      type: 'NETWORK_ERROR',
      source: 'useThemeQuery:/settings/theme',
      at: Date.now(),
      message: 'Theme fetch network fail',
    });
    return loadCachedTheme(true) || defaultTheme;
  }
};

/**
 * Query key for theme
 * @deprecated Use QUERY_KEYS.settings.theme instead
 */
export const THEME_QUERY_KEY = QUERY_KEYS.settings.theme;

/**
 * Custom hook to fetch theme with React Query
 * - Cache: 5-10 minutes (staleTime: 5 minutes, cacheTime: 10 minutes)
 * - Automatic refetch on window focus
 * - Optimistic updates via setQueryData
 */
export const useThemeQuery = () => {
  const initial = loadCachedTheme() || defaultTheme;
  return useQuery({
    queryKey: QUERY_KEYS.settings.theme,
    queryFn: fetchTheme,
    initialData: initial,
    staleTime: 5 * 60 * 1000, // 5 minutes - data is fresh for 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime) - keep in cache for 10 minutes
    refetchOnWindowFocus: true, // Refetch when window gains focus (to pick up changes from other users)
    retry: (failureCount, error) => {
      const kind = (error as { kind?: string } | undefined)?.kind;
      if (kind === 'auth') return false;
      if (kind === 'network') return failureCount < 3;
      if (kind === 'server') return failureCount < 2;
      return failureCount < 2;
    },
    retryDelay: (attempt) => Math.min(1000 * 2 ** Math.max(0, attempt - 1), 8000),
  });
};

/**
 * Hook to get queryClient for manual cache updates
 * Use this in updateTheme to setQueryData after PUT
 */
export const useThemeQueryClient = () => {
  return useQueryClient();
};

